//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import _ from 'underscore';

import DateString from '@models/DateString';
import { isDefined } from '@utils/filterPredicates';
import { mergeObjects } from '@utils/mergeObjects';
import { NormalizedScheduledPins } from 'app/pinterest/redux/schedule/pinterestScheduleModel';

export const PINS_FETCH_PAST_DAYS_BUFFER = 7;
export const PINS_FETCH_UPCOMING_DAYS_BUFFER = 14;

/**
 * Merges a list of normalized scheduled pins together and re-sorts the ordered ids by scheduled time ascending.
 */
export const mergeScheduledPinsLists = (
  scheduledItemLists: NormalizedScheduledPins[],
): NormalizedScheduledPins => {
  const mergedById = mergeObjects(
    scheduledItemLists.map((scheduledItems) => scheduledItems.byId),
  );
  const mergedItems = PojoMap.values(mergedById).filter(isDefined);

  const mergedOrderedIds = _.sortBy(mergedItems, 'scheduledTime').map(
    (item) => item.id,
  );

  return {
    byId: mergedById,
    orderedIds: mergedOrderedIds,
  };
};

/**
 * Get the default pin start and end fetch dates for a given starting date.
 *
 * Fetch posts from one week prior to the start date so that posts are already loaded when navigating back
 * to see recent items.
 *
 * Fetch posts up to two weeks after the start date.
 */
export const getPinFetchStartAndEndDate = (startDate: DateString) => {
  return {
    defaultStartDate: DateString.addDays(
      startDate,
      -PINS_FETCH_PAST_DAYS_BUFFER,
    ),
    defaultEndDate: DateString.addDays(
      startDate,
      PINS_FETCH_UPCOMING_DAYS_BUFFER,
    ),
  };
};

/**
 * Returns true if the new start date (or new visible days) fall outside of the window of last fetched posts.
 */
export const isStartDateOutsideOfLastFetchedWindow = (
  newStartDate: DateString,
  lastFetchedStartDate: DateString,
) => {
  const isNewDateBeforeLastFetchedWindow =
    DateString.getDayOffset(newStartDate, lastFetchedStartDate) >
    PINS_FETCH_PAST_DAYS_BUFFER;

  // Add a 7 day buffer to ensure posts are loaded for the entire week of the selected start date
  const isNewDateAfterLastFetchedWindow =
    DateString.getDayOffset(lastFetchedStartDate, newStartDate) >
    PINS_FETCH_UPCOMING_DAYS_BUFFER - 7;

  return isNewDateBeforeLastFetchedWindow || isNewDateAfterLastFetchedWindow;
};
